import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMobileAlt,
  faBuilding,
  faSave,
  faUndo,
  faCheckCircle,
  faTimesCircle,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import practiceService from '../../services/practiceService';
import mobileAccessService from '../../services/mobileAccessService';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import StatusBadge from '../../components/Common/StatusBadge/StatusBadge';
import SkeletonLoader from '../../components/SkeletonLoader/SkeletonLoader';
import { exportElementToPdf } from '../../utils/reportExportUtils';
import '../PracticeManagement/GlobalDashboard/GlobalDashboard.css';
import './MobileAppAccess.css';

const SECTIONS = [
  { key: 'patients', label: 'Patients', userKey: 'patients' },
  { key: 'providers', label: 'Providers', userKey: 'providers' },
  { key: 'caregivers', label: 'Caregivers', userKey: 'caregivers' }
];

const STATUS_FILTER_OPTIONS = [
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' }
];

const USER_TYPE_FILTER_OPTIONS = [
  { value: 'patients', label: 'Patients' },
  { value: 'providers', label: 'Providers' },
  { value: 'caregivers', label: 'Caregivers' }
];

const getUserId = (user) => Number(user.user_id);

const getUserLabel = (user) =>
  user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unnamed';

const MobileAppAccess = ({ embedded = false } = {}) => {
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [practices, setPractices] = useState([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState('');
  const [practiceName, setPracticeName] = useState('');
  const [usersBySection, setUsersBySection] = useState({
    patients: [],
    providers: [],
    caregivers: []
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [initialSelectedIds, setInitialSelectedIds] = useState(new Set());
  const [loadingPractices, setLoadingPractices] = useState(true);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (embedded) {
      updateBreadcrumbs([
        { label: 'Settings', path: '/settings' },
        { label: 'Mobile App', path: '/settings?tab=mobile-app-access', active: true }
      ]);
    } else {
      updateBreadcrumbs([
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Mobile App Access', path: '/mobile-app-access', active: true }
      ]);
    }
    return () => clearBreadcrumbs();
  }, [updateBreadcrumbs, clearBreadcrumbs, embedded]);

  useEffect(() => {
    const loadPractices = async () => {
      try {
        setLoadingPractices(true);
        const response = await practiceService.getPractices({ limit: 1000, status: 'Active' });
        let list = [];
        const payload = response?.data;
        if (payload?.success && payload?.data?.practices) {
          list = payload.data.practices;
        } else if (payload?.success && Array.isArray(payload?.data)) {
          list = payload.data;
        } else if (Array.isArray(payload)) {
          list = payload;
        }
        setPractices(list);
      } catch (err) {
        console.error('Failed to load practices:', err);
        setError('Failed to load practices.');
      } finally {
        setLoadingPractices(false);
      }
    };

    loadPractices();
  }, []);

  const allUsers = useMemo(
    () => [...usersBySection.patients, ...usersBySection.providers, ...usersBySection.caregivers],
    [usersBySection]
  );

  const loadMobileAccess = useCallback(async (practiceId) => {
    if (!practiceId) return;

    try {
      setLoadingAccess(true);
      setError('');
      setSearchTerm('');
      setStatusFilter('');
      setUserTypeFilter('');
      const response = await mobileAccessService.getPracticeMobileAccess(practiceId);
      const data = response?.data?.data;

      if (!data) {
        throw new Error('Invalid response');
      }

      setPracticeName(data.practice_name || '');
      setUsersBySection({
        patients: data.patients || [],
        providers: data.providers || [],
        caregivers: data.caregivers || []
      });

      const granted = new Set();
      [...(data.patients || []), ...(data.providers || []), ...(data.caregivers || [])].forEach((user) => {
        if (user.mobile_app_access) {
          granted.add(getUserId(user));
        }
      });
      setSelectedIds(new Set(granted));
      setInitialSelectedIds(new Set(granted));
    } catch (err) {
      console.error('Failed to load mobile access:', err);
      setError('Failed to load mobile access settings for this practice.');
      setUsersBySection({ patients: [], providers: [], caregivers: [] });
      setSelectedIds(new Set());
      setInitialSelectedIds(new Set());
    } finally {
      setLoadingAccess(false);
    }
  }, []);

  const handlePracticeChange = (event) => {
    const practiceId = event.target.value;
    setSelectedPracticeId(practiceId);
    if (practiceId) {
      loadMobileAccess(practiceId);
    } else {
      setPracticeName('');
      setUsersBySection({ patients: [], providers: [], caregivers: [] });
      setSelectedIds(new Set());
      setInitialSelectedIds(new Set());
      setSearchTerm('');
      setStatusFilter('');
      setUserTypeFilter('');
    }
  };

  const filterUsers = useCallback((users) => {
    let filtered = users;

    if (statusFilter === 'enabled') {
      filtered = filtered.filter((user) => selectedIds.has(getUserId(user)));
    } else if (statusFilter === 'disabled') {
      filtered = filtered.filter((user) => !selectedIds.has(getUserId(user)));
    }

    if (!searchTerm.trim()) return filtered;

    const term = searchTerm.trim().toLowerCase();
    return filtered.filter((user) => {
      const name = getUserLabel(user).toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [searchTerm, statusFilter, selectedIds]);

  const visibleSections = useMemo(() => {
    if (!userTypeFilter) return SECTIONS;
    return SECTIONS.filter((section) => section.userKey === userTypeFilter);
  }, [userTypeFilter]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setUserTypeFilter('');
  };

  const hasActiveFilters = Boolean(searchTerm.trim() || statusFilter || userTypeFilter);

  const getExportRows = useCallback(() => {
    const rows = [];

    visibleSections.forEach((section) => {
      const users = filterUsers(usersBySection[section.userKey] || []);
      users.forEach((user) => {
        const userId = getUserId(user);
        rows.push({
          name: getUserLabel(user),
          email: user.email || '—',
          type: section.label,
          status: selectedIds.has(userId) ? 'Enabled' : 'Disabled'
        });
      });
    });

    return rows;
  }, [visibleSections, filterUsers, usersBySection, selectedIds]);

  const handleExportPdf = async () => {
    const rows = getExportRows();

    if (!rows.length) {
      toast.info('No users match the current filters to export.');
      return;
    }

    const statusLabel = statusFilter
      ? STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label
      : 'Status';
    const typeLabel = userTypeFilter
      ? USER_TYPE_FILTER_OPTIONS.find((o) => o.value === userTypeFilter)?.label
      : 'Users Group';
    const generatedAt = new Date().toLocaleString();
    const safePracticeName = (practiceName || 'practice').replace(/[^\w\s-]/g, '').trim();

    const element = document.createElement('div');
    element.style.padding = '24px';
    element.style.fontFamily = 'Arial, sans-serif';
    element.style.color = '#1e293b';
    element.innerHTML = `
      <div style="margin-bottom: 20px;">
        <h2 style="margin: 0 0 8px; font-size: 20px; color: var(--ev-btn-primary);">Mobile App Access Report</h2>
        <p style="margin: 0 0 4px; font-size: 13px;"><strong>Practice:</strong> ${practiceName || '—'}</p>
        <p style="margin: 0 0 4px; font-size: 13px;"><strong>Status:</strong> ${statusLabel}</p>
        <p style="margin: 0 0 4px; font-size: 13px;"><strong>User Type:</strong> ${typeLabel}</p>
        ${searchTerm.trim() ? `<p style="margin: 0 0 4px; font-size: 13px;"><strong>Search:</strong> ${searchTerm.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>` : ''}
        <p style="margin: 0; font-size: 12px; color: #64748b;">Generated: ${generatedAt}</p>
      </div>
      <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
        <thead>
          <tr style="background: #f1f5f9;">
            <th style="border: 1px solid #e2e8f0; padding: 8px; text-align: left;">#</th>
            <th style="border: 1px solid #e2e8f0; padding: 8px; text-align: left;">Name</th>
            <th style="border: 1px solid #e2e8f0; padding: 8px; text-align: left;">Email</th>
            <th style="border: 1px solid #e2e8f0; padding: 8px; text-align: left;">User Type</th>
            <th style="border: 1px solid #e2e8f0; padding: 8px; text-align: left;">Mobile Access</th>
          </tr>
        </thead>
        <tbody>
          ${rows.map((row, index) => `
            <tr>
              <td style="border: 1px solid #e2e8f0; padding: 8px;">${index + 1}</td>
              <td style="border: 1px solid #e2e8f0; padding: 8px;">${row.name.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
              <td style="border: 1px solid #e2e8f0; padding: 8px;">${row.email.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
              <td style="border: 1px solid #e2e8f0; padding: 8px;">${row.type}</td>
              <td style="border: 1px solid #e2e8f0; padding: 8px; color: ${row.status === 'Enabled' ? 'var(--ev-btn-primary)' : '#94a3b8'};">${row.status}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      <p style="margin-top: 16px; font-size: 12px; color: #64748b;">Total Records: ${rows.length}</p>
    `;

    try {
      setExporting(true);
      document.body.appendChild(element);
      await new Promise((resolve) => setTimeout(resolve, 200));
      await exportElementToPdf(
        element,
        `Mobile-App-Access-${safePracticeName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`,
        { jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' } }
      );
      toast.success('Report exported successfully');
    } catch (err) {
      console.error('Failed to export mobile access PDF:', err);
      toast.error('Failed to download PDF');
    } finally {
      if (element.parentNode) {
        document.body.removeChild(element);
      }
      setExporting(false);
    }
  };

  const toggleUser = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const isSectionFullySelected = (sectionKey) => {
    const users = usersBySection[sectionKey] || [];
    if (users.length === 0) return false;
    return users.every((user) => selectedIds.has(getUserId(user)));
  };

  const toggleSectionAll = (sectionKey) => {
    const users = usersBySection[sectionKey] || [];
    const allSelected = isSectionFullySelected(sectionKey);

    setSelectedIds((prev) => {
      const next = new Set(prev);
      users.forEach((user) => {
        const id = getUserId(user);
        if (allSelected) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });
      return next;
    });
  };

  const hasChanges = useMemo(() => {
    if (selectedIds.size !== initialSelectedIds.size) return true;
    for (const id of selectedIds) {
      if (!initialSelectedIds.has(id)) return true;
    }
    return false;
  }, [selectedIds, initialSelectedIds]);

  const handleReset = () => {
    setSelectedIds(new Set(initialSelectedIds));
  };

  const handleSave = async () => {
    if (!selectedPracticeId) return;

    try {
      setSaving(true);
      const response = await mobileAccessService.updatePracticeMobileAccess(
        selectedPracticeId,
        Array.from(selectedIds)
      );

      if (response?.data?.success) {
        toast.success('Mobile app access saved successfully');
        const data = response.data.data;
        setUsersBySection({
          patients: data.patients || [],
          providers: data.providers || [],
          caregivers: data.caregivers || []
        });
        setInitialSelectedIds(new Set(selectedIds));
      } else {
        throw new Error(response?.data?.message || 'Save failed');
      }
    } catch (err) {
      console.error('Failed to save mobile access:', err);
      toast.error(err?.response?.data?.message || 'Failed to save mobile app access');
    } finally {
      setSaving(false);
    }
  };

  const renderUserList = (users, sectionKey) => {
    const filtered = filterUsers(users);

    if (!users.length) {
      return (
        <div className="maa-empty-state">
          <FontAwesomeIcon icon={faTimesCircle} className="maa-empty-icon" />
          <span>No users found in this practice.</span>
        </div>
      );
    }

    if (!filtered.length) {
      const activeFilters = searchTerm.trim() || statusFilter || userTypeFilter;
      return (
        <div className="maa-empty-state">
          <FontAwesomeIcon icon={faSearch} className="maa-empty-icon" />
          <span>
            {activeFilters
              ? 'No users match the current filters.'
              : 'No users match your search.'}
          </span>
        </div>
      );
    }

    return (
      <div className="maa-user-list">
        {filtered.map((user, index) => {
          const userId = getUserId(user);
          const label = getUserLabel(user);
          const isSelected = selectedIds.has(userId);

          return (
            <label
              key={userId}
              className={`maa-user-item${index % 2 === 1 ? ' maa-user-item--alt' : ''}`}
              htmlFor={`mobile-user-${userId}`}
            >
              <input
                id={`mobile-user-${userId}`}
                type="checkbox"
                className="maa-user-checkbox"
                checked={isSelected}
                onChange={() => toggleUser(userId)}
              />
              <div className="maa-avatar-wrap">
                <StatusBadge status={user.status} className="maa-user-status-badge" />
              </div>
              <div className="maa-user-info">
                <div className="maa-user-name">{label}</div>
                {user.email && <div className="maa-user-email">{user.email}</div>}
              </div>
              <span className={`maa-access-badge${isSelected ? ' maa-access-badge--on' : ''}`}>
                <FontAwesomeIcon icon={isSelected ? faCheckCircle : faTimesCircle} />
                {isSelected ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`dashboard-container maa-page${embedded ? ' maa-page--embedded' : ''}`}>
      {!embedded && (
        <div className="dashboard-header">
          <h2 className="dashboard-title">Mobile App Access</h2>
        </div>
      )}

      <div className="dashboard-summary-card mb-4">
        <div className="summary-title">Access Summary</div>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-value color-black">{allUsers.length}</span>
            <span className="summary-label font-semibold">Total Users</span>
          </div>
          <div className="summary-item">
            <span className="summary-value color-black">{selectedIds.size}</span>
            <span className="summary-label font-semibold">Mobile Enabled</span>
          </div>
          <div className="summary-item">
            <span className="summary-value color-black">{usersBySection.patients.length}</span>
            <span className="summary-label font-semibold">Patients</span>
          </div>
          <div className="summary-item">
            <span className="summary-value color-black">
              {usersBySection.providers.length + usersBySection.caregivers.length}
            </span>
            <span className="summary-label font-semibold">Staff</span>
          </div>
        </div>
      </div>

      <div className="maa-filters-wrap">
        <div
          className={`dashboard-filters maa-dashboard-filters${
            selectedPracticeId ? ' maa-dashboard-filters--full' : ' maa-dashboard-filters--practice-only'
          }`}
        >
          {selectedPracticeId ? (
            <>
              {!loadingAccess && (
                <>
                  <div className="search-box-wrap">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" aria-hidden="true" />
                    <input
                      id="mobile-app-access-search"
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      autoComplete="off"
                      aria-label="Search users"
                    />
                  </div>

                  <select
                    className="dashboard-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label="Filter by mobile access status"
                  >
                    <option value="">Status</option>
                    {STATUS_FILTER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <select
                    className="dashboard-select"
                    value={userTypeFilter}
                    onChange={(e) => setUserTypeFilter(e.target.value)}
                    aria-label="Filter by user group"
                  >
                    <option value="">Users Group</option>
                    {USER_TYPE_FILTER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <select
                className="dashboard-select maa-practice-select-field"
                value={selectedPracticeId}
                onChange={handlePracticeChange}
                disabled={loadingPractices}
                aria-label="Select practice"
              >
                <option value="">Practice</option>
                {practices.map((practice) => (
                  <option key={practice.id} value={practice.id}>
                    {practice.practice_name}
                  </option>
                ))}
              </select>

              {!loadingAccess && (
                <button
                  type="button"
                  className="btn maa-export-btn"
                  onClick={handleExportPdf}
                  disabled={exporting}
                  aria-label="Export filtered report as PDF"
                >
                  {exporting ? 'Exporting...' : 'Export'}
                </button>
              )}
            </>
          ) : (
            <select
              className="dashboard-select maa-practice-select-field maa-practice-select-field--end"
              value={selectedPracticeId}
              onChange={handlePracticeChange}
              disabled={loadingPractices}
              aria-label="Select practice"
            >
              <option value="">
                {loadingPractices ? 'Loading practices...' : 'Practice'}
              </option>
              {practices.map((practice) => (
                <option key={practice.id} value={practice.id}>
                  {practice.practice_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedPracticeId && !loadingAccess && hasActiveFilters && (
          <button
            type="button"
            className="maa-clear-all"
            onClick={handleClearFilters}
            title="Clear all filters and search criteria"
          >
            Clear All
          </button>
        )}
      </div>

      {error && (
        <div className="maa-alert maa-alert-danger" role="alert">
          {error}
        </div>
      )}

      {!selectedPracticeId && !loadingPractices && (
        <div className="maa-placeholder-card">
          <FontAwesomeIcon icon={faBuilding} className="maa-placeholder-icon" />
          <h4>Select a Practice</h4>
          <p>Choose a practice from the dropdown above to manage which users can access the mobile app.</p>
        </div>
      )}

      {selectedPracticeId && loadingAccess && (
        <div className="maa-loading-card">
          <SkeletonLoader type="list" count={8} />
        </div>
      )}

      {selectedPracticeId && !loadingAccess && (
        <>
          <div className={`maa-sections-grid maa-sections-grid--${visibleSections.length}`}>
            {visibleSections.map((section) => {
              const users = usersBySection[section.userKey] || [];
              const sectionSelectedCount = users.filter((u) => selectedIds.has(getUserId(u))).length;

              return (
                <div key={section.key} className="maa-section-card">
                  <div className="maa-section-card-header">
                    <div className="maa-section-title-wrap">
                      <div>
                        <h5 className="maa-section-title">{section.label}</h5>
                        <span className="maa-section-count">
                          {sectionSelectedCount} of {users.length} enabled
                        </span>
                      </div>
                    </div>
                    <label className="maa-select-all">
                      <input
                        type="checkbox"
                        checked={isSectionFullySelected(section.userKey)}
                        onChange={() => toggleSectionAll(section.userKey)}
                        disabled={users.length === 0}
                      />
                      Select All
                    </label>
                  </div>
                  <div className="maa-section-card-body">
                    {renderUserList(users, section.userKey)}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="maa-footer-bar">
            <div className="maa-footer-summary">
              <FontAwesomeIcon icon={faMobileAlt} className="maa-footer-icon" />
              <span>
                <strong>{selectedIds.size}</strong> of <strong>{allUsers.length}</strong> users selected for mobile access
              </span>
            </div>
            <div className="maa-footer-actions">
              {/* <button
                type="button"
                className="btn btn-outline-secondary maa-btn-reset"
                onClick={handleReset}
                disabled={!hasChanges || saving}
              >
                <FontAwesomeIcon icon={faUndo} className="me-2" />
                Reset
              </button> */}
              <button
                type="button"
                className="btn maa-btn-save"
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {saving ? 'Saving...' : 'Save Access Settings'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileAppAccess;
